import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { ChallengeCard } from '../components/Challenges/ChallengeCard';
import { NewsCard } from '../components/News/NewsCard';
import { Trophy, Plus } from 'lucide-react';
import { useAdmin } from '../hooks/useAdmin';
import { ChallengeForm } from '../components/Admin/ChallengeForm';
import type { Challenge } from '../types';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

export function ChallengesNews() {
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();
  const [showChallengeForm, setShowChallengeForm] = useState(false);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch challenges
      const { data: challengesData, error: challengesError } = await supabase
        .from('challenges')
        .select('*, challenge_participants(count)')
        .eq('is_active', true)
        .order('start_date', { ascending: false });

      if (challengesError) throw new Error('Failed to fetch challenges');

      setChallenges(challengesData.map(c => ({
        ...c,
        startDate: new Date(c.start_date),
        endDate: new Date(c.end_date),
        participants: c.challenge_participants[0]?.count || 0,
        isActive: c.is_active
      })));
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load content. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinChallenge = async (challengeId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('challenge_participants')
        .insert({
          challenge_id: challengeId,
          user_id: user?.id
        });

      if (error) throw error;
      await fetchData();
    } catch (error: any) {
      console.error('Error joining challenge:', error);
      alert('Failed to join challenge. Please try again.');
    }
  };

  const handleEditChallenge = async (challenge: Challenge) => {
    setShowChallengeForm(true);
    // TODO: Pass challenge data to form for editing
  };

  const handleArchiveChallenge = async (challenge: Challenge) => {
    try {
      const { error } = await supabase
        .from('challenges')
        .update({ is_active: false })
        .eq('id', challenge.id);

      if (error) throw error;
      await fetchData();
    } catch (error) {
      console.error('Error archiving challenge:', error);
      alert('Failed to archive challenge. Please try again.');
    }
  };

    // TODO: Pass news data to form for editing
  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Challenges</h1>
          {isAdmin && (
            <button
              onClick={() => setShowChallengeForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#bbdde5] text-black rounded-xl hover:opacity-90 transition-opacity"
            >
              <Plus className="w-4 h-4" />
              <span className="text-xs">New Challenge</span>
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-xl mb-6">
            <p className="text-sm">
              {error}
            </p>
          </div>
        )}

        <div className="space-y-4">
            {loading ? (
              <div className="text-center py-8 text-gray-400">Loading...</div>
            ) : challenges.length === 0 ? (
              <div className="text-center py-8 text-gray-400">No active challenges</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {challenges.map(challenge => (
                  <ChallengeCard
                    key={challenge.id}
                    challenge={challenge}
                    isAdmin={isAdmin}
                    onEdit={handleEditChallenge}
                    onArchive={handleArchiveChallenge}
                    onJoin={() => handleJoinChallenge(challenge.id)}
                  />
                ))}
              </div>
            )}
        </div>
      </div>
      
      {showChallengeForm && (
        <ChallengeForm
          onClose={() => setShowChallengeForm(false)}
          onSuccess={fetchData}
        />
      )}
    </Layout>
  );
}